import { AppService } from '@services/app/app.service';
import { OperationService } from '@services/operation/operation.service';
import { UiService } from '@services/ui/ui.service';
import { NavigatorService } from '@services/navigator/navigator.service';
import { Injectable } from '@angular/core';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';
import { from, Observable, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { RouteCodes } from '@core/config/route-codes';

@Injectable({
  providedIn: 'root'
})
export class OperationGuard implements CanActivate {
  private loaderInvoked: boolean;

  constructor(
    private navigator: NavigatorService,
    private uiService: UiService,
    private appService: AppService,
    private operationService: OperationService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return this.showLoader().pipe(
      mergeMap(() => this.resumeOrContinue()),
      catchError(err => of(this.navigator.createErrorUrlTree(err))),
      mergeMap(output => this.hideLoader(output))
    );
  }

  /**
   * ripristina i dati della position e del wizard se non sono caricati (in caso di F5)
   * altrimenti prosegue senza fare alcun caricamento
   */
  private resumeOrContinue(): Observable<boolean | UrlTree> {
    if (!this.operationService.isResumeNeeded()) {
      return of(true);
    }

    if (this.appService.isStandalone()) {
      return this.resumeAsStandalone();
    } else {
      return this.resumeAsOperation();
    }
  }

  /**
   * ripristina l'operazione
   */
  private resumeAsOperation(): Observable<boolean> {
    this.operationService.setupFromAuth();

    return this.operationService.resume();
  }

  /**
   * ripristina l'operazione in app standalone
   */
  private resumeAsStandalone(): Observable<boolean | UrlTree> {
    if (!this.operationService.isSetupValid()) {
      return of(this.navigator.createMainRouteUrlTree(RouteCodes.reservedArea));
    } else {
      return this.operationService.resume();
    }
  }

  /**
   * visualizza loader
   */
  private showLoader(): Observable<void> {
    this.loaderInvoked = true;

    return from(this.uiService.showAppLoader('lbl.loading'));
  }

  /**
   * nasconde loader
   */
  private hideLoader(output: boolean | UrlTree): Observable<boolean | UrlTree> {
    if (this.loaderInvoked) {
      this.loaderInvoked = false;

      return from(this.uiService.hideAppLoader()).pipe(map(() => output));
    } else {
      return of(output);
    }
  }
}
